/**
 * M32106 Resignation of Director — Register 용 directors[] 빌드/해석.
 * TASK_DATA.formData.directors 또는 presetForm.values(dir{n}_*) 에서 추출.
 */

export type ResignRegisterDirector = {
  fullName: string;
  nricOrPassport: string;
  residentialAddress: string;
  dateOfCessation: string;
  appointmentDate: string;
  contactCd: string;
  officerIdx: number | null;
  reasonForCessation: string;
};

function trim(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function toOfficerIdx(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Studio values + rows → Register Detail / L_OFFICER 갱신용 directors[] */
export function buildResignRegisterDirectors(
  values: Record<string, any>,
  rows: number[],
  reasons?: Record<string | number, string>,
): ResignRegisterDirector[] {
  const out: ResignRegisterDirector[] = [];
  for (const id of rows) {
    const fullName = trim(values[`dir${id}_full_name`]);
    if (!fullName) continue;
    const reason =
      trim(reasons?.[id]) ||
      trim(reasons?.[String(id)]) ||
      trim(values[`dir${id}_reason`]) ||
      trim(values[`dir${id}_reason_for_cessation`]);
    out.push({
      fullName,
      nricOrPassport: trim(values[`dir${id}_ic_passport_no`]),
      residentialAddress: trim(values[`dir${id}_residential_address`]),
      dateOfCessation: trim(values[`dir${id}_date_of_cessation`]),
      appointmentDate: trim(values[`dir${id}_appointment_date`]),
      contactCd: trim(values[`dir${id}_contact_cd`]),
      officerIdx: toOfficerIdx(values[`dir${id}_officer_idx`]),
      reasonForCessation: reason,
    });
  }
  return out;
}

/** TASK_DATA.formData 에서 사임 이사 목록 해석 (legacy presetForm-only 포함) */
export function resolveResignRegisterDirectors(
  formData: Record<string, any> | null | undefined,
): ResignRegisterDirector[] {
  const fd = formData && typeof formData === 'object' ? formData : {};
  const fromRoot = Array.isArray(fd.directors) ? fd.directors : [];
  if (fromRoot.length > 0) {
    return fromRoot
      .map((row: any) => ({
        fullName: trim(row?.fullName || row?.name),
        nricOrPassport: trim(row?.nricOrPassport || row?.icPassportNo),
        residentialAddress: trim(row?.residentialAddress),
        dateOfCessation: trim(
          row?.dateOfCessation || row?.cessationDate || row?.date_of_cessation,
        ),
        appointmentDate: trim(row?.appointmentDate || row?.dateOfAppointment),
        contactCd: trim(row?.contactCd || row?.contact_cd),
        officerIdx: toOfficerIdx(row?.officerIdx || row?.officer_idx),
        reasonForCessation: trim(row?.reasonForCessation || row?.reason),
      }))
      .filter((d) => d.fullName);
  }

  const preset = fd.presetForm && typeof fd.presetForm === 'object' ? fd.presetForm : null;
  const values = (preset?.values && typeof preset.values === 'object' ? preset.values : null)
    || (fd.values && typeof fd.values === 'object' ? fd.values : null)
    || {};
  const local = preset?.local && typeof preset.local === 'object' ? preset.local : {};
  const rowsRaw = Array.isArray(local.rows) && local.rows.length ? local.rows : [1];
  const rows = rowsRaw.map((r: any) => Number(r)).filter((n: number) => Number.isFinite(n) && n > 0);
  return buildResignRegisterDirectors(values, rows.length ? rows : [1], local.reasons || {});
}
